import { useCallback, useEffect, useState, type MouseEvent } from 'react';

import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';

interface Props {
  src: string;
  alt: string;
  /** Todas las fotos del producto, para poder pasar de una a otra en pantalla completa. */
  fotos: string[];
  actual: number;
  onIr: (indice: number) => void;
  altoDeLaLupa?: number;
  anchoDeLaLupa?: number;
  aumento?: number;
}

/**
 * La foto grande del producto, con lupa al pasar el mouse y pantalla completa al hacer clic.
 *
 * La lupa no es un adorno: son repuestos donde la diferencia entre dos piezas es una rosca o un
 * milímetro de diámetro, y quien compra necesita mirar de cerca antes de pedir.
 */
export function LupaDeImagen({
  src,
  alt,
  fotos,
  actual,
  onIr,
  altoDeLaLupa = 300,
  anchoDeLaLupa = 300,
  aumento = 2.5,
}: Props) {
  const [conLupa, setConLupa] = useState(false);
  const [tamanio, setTamanio] = useState<[number, number]>([0, 0]);
  const [enPantalla, setEnPantalla] = useState<[number, number]>([0, 0]);
  const [enLaFoto, setEnLaFoto] = useState<[number, number]>([0, 0]);
  const [completa, setCompleta] = useState(false);

  const anterior = useCallback(() => {
    if (actual > 0) onIr(actual - 1);
  }, [actual, onIr]);

  const siguiente = useCallback(() => {
    if (actual < fotos.length - 1) onIr(actual + 1);
  }, [actual, fotos.length, onIr]);

  const cerrar = useCallback(() => setCompleta(false), []);

  // En pantalla completa las flechas del teclado pasan las fotos y Escape cierra. Es lo que
  // cualquiera intenta sin que se lo digan.
  useEffect(() => {
    if (!completa) return;

    function alApretar(evento: KeyboardEvent) {
      if (evento.key === 'ArrowLeft') anterior();
      else if (evento.key === 'ArrowRight') siguiente();
      else if (evento.key === 'Escape') cerrar();
    }

    window.addEventListener('keydown', alApretar);
    return () => window.removeEventListener('keydown', alApretar);
  }, [completa, anterior, siguiente, cerrar]);

  function alEntrar(evento: MouseEvent<HTMLImageElement>) {
    const { width, height } = evento.currentTarget.getBoundingClientRect();
    setTamanio([width, height]);
    setConLupa(true);
  }

  function alMover(evento: MouseEvent<HTMLImageElement>) {
    const { top, left } = evento.currentTarget.getBoundingClientRect();

    setEnPantalla([evento.clientX, evento.clientY]);
    setEnLaFoto([evento.pageX - left - window.scrollX, evento.pageY - top - window.scrollY]);
  }

  const [ancho, alto] = tamanio;
  const [x, y] = enPantalla;
  const [fotoX, fotoY] = enLaFoto;

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          // Sin fondo propio: el blanco lo pone el cuadro de GaleriaDeProducto, que es quien
          // decide como se ve la foto grande. Aca habia un negro que lo tapaba, asi que el
          // cuadro quedaba negro con la foto blanca flotando en el medio.
          backgroundColor: 'transparent',
        }}
      >
        <Box
          component="img"
          src={src}
          alt={alt}
          onMouseEnter={alEntrar}
          onMouseLeave={() => setConLupa(false)}
          onMouseMove={alMover}
          onClick={() => setCompleta(true)}
          sx={{
            objectFit: 'contain',
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            cursor: 'pointer',
            // El otro extremo del viaje desde la grilla del catálogo. Acá va siempre puesto
            // porque en la ficha hay una sola foto grande: el nombre no puede repetirse.
            viewTransitionName: 'foto-producto',
          }}
        />

        <Box
          sx={{
            display: conLupa && !completa ? 'block' : 'none',
            position: 'fixed',
            pointerEvents: 'none',
            height: altoDeLaLupa,
            width: anchoDeLaLupa,
            border: '2px solid #3483fa',
            backgroundColor: '#fff',
            borderRadius: '8px',
            backgroundImage: `url('${src}')`,
            backgroundRepeat: 'no-repeat',
            top: `${y}px`,
            left: `${x}px`,
            transform: 'translate(-50%, -50%)',
            backgroundSize: `${ancho * aumento}px ${alto * aumento}px`,
            backgroundPositionX: `${-fotoX * aumento + anchoDeLaLupa / 2}px`,
            backgroundPositionY: `${-fotoY * aumento + altoDeLaLupa / 2}px`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            zIndex: 9999,
          }}
        />
      </Box>

      {completa && (
        <Box
          role="button"
          tabIndex={0}
          onClick={cerrar}
          onKeyDown={evento => {
            if (evento.key === 'Enter' || evento.key === ' ') cerrar();
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10_000,
          }}
        >
          <IconButton
            onClick={cerrar}
            aria-label="Cerrar"
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: '#000',
              zIndex: 10_001,
              '&:hover': { backgroundColor: '#fff' },
            }}
          >
            <Close />
          </IconButton>

          {fotos.length > 1 && actual > 0 && (
            <IconButton
              aria-label="Foto anterior"
              onClick={evento => {
                evento.stopPropagation();
                anterior();
              }}
              sx={{
                position: 'absolute',
                left: 20,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                color: '#000',
                zIndex: 10_001,
                '&:hover': { backgroundColor: '#fff' },
              }}
            >
              <ChevronLeft fontSize="large" />
            </IconButton>
          )}

          <Box
            component="img"
            src={src}
            alt={alt}
            role="presentation"
            tabIndex={-1}
            onClick={evento => evento.stopPropagation()}
            sx={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }}
          />

          {fotos.length > 1 && actual < fotos.length - 1 && (
            <IconButton
              aria-label="Foto siguiente"
              onClick={evento => {
                evento.stopPropagation();
                siguiente();
              }}
              sx={{
                position: 'absolute',
                right: 20,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                color: '#000',
                zIndex: 10_001,
                '&:hover': { backgroundColor: '#fff' },
              }}
            >
              <ChevronRight fontSize="large" />
            </IconButton>
          )}

          {fotos.length > 1 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 20,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: '#fff',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              {actual + 1} / {fotos.length}
            </Box>
          )}
        </Box>
      )}
    </>
  );
}
